/**
 * App.tsx
 * Root application component. Handles routing between:
 * - Task Pane routes (#/home, #/quiz/*, #/class, #/ai)
 * - Student Portal routes (#/join, #/student/*)
 * - Presenter Overlay (conditionally rendered on top of everything)
 *
 * Uses MemoryRouter — Office.js disables window.history.pushState/replaceState
 * (even outside the task pane, e.g. for students opening the portal link),
 * which crashes HashRouter/BrowserRouter on init. The initial route is seeded
 * from window.location.hash once at startup so deep links like #/join still
 * work without calling the broken history APIs.
 */

import React, { useEffect } from 'react';
import { MemoryRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useOfficeReady } from '@hooks/useOfficeReady';
import { useActiveView } from '@hooks/useActiveView';
import { useRoom } from '@hooks/useRoom';
import { useRoster } from '@hooks/useRoster';
import { useAppStore } from '@store/appStore';
import { loadActiveSession } from '@office/settingsService';

// Task Pane UI
import TaskPane from '@components/taskpane/TaskPane';

// Student Portal
import StudentApp from '@student/StudentApp';

// Presenter Overlay (portaled, rendered over the full window)
import PresenterOverlay from '@components/presenter/PresenterOverlay';

// Loading screen
import LoadingSpinner from '@components/shared/LoadingSpinner';

function AppInner(): React.ReactElement {
  const isOfficeReady = useOfficeReady();
  const presenterOverlayVisible = useAppStore((s) => s.presenterOverlayVisible);
  const activeRoom = useAppStore((s) => s.activeRoom);
  const activeClassId = useAppStore((s) => s.activeClassId);

  // Register Office view polling after Office is ready
  useActiveView();

  // Sync active room subscription
  useRoom(activeRoom?.roomCode ?? null);

  // Sync class roster
  useRoster(activeClassId);

  // Restore the live session after a task pane reload. activeRoom is
  // in-memory, so opening a ribbon button (which reloads the pane) would
  // otherwise look like the session ended. The room code is persisted in
  // document settings, so re-hydrate from there and re-subscribe.
  useEffect(() => {
    if (!isOfficeReady) return;
    // Only the teacher task pane runs inside the Office host. The student
    // portal opens in a plain browser where Office.context is undefined,
    // so guard against touching document settings there.
    if (typeof Office === 'undefined' || !Office.context?.document) return;
    const store = useAppStore.getState();
    if (store.activeRoom) return;
    try {
      const session = loadActiveSession();
      if (session) {
        store.setActiveRoom({ roomCode: session.roomCode } as never);
        store.setActiveClassId(session.classId);
      }
    } catch (err) {
      console.warn('[GuroHub] Session restore skipped:', err);
    }
  }, [isOfficeReady]);

  // Best-effort: open the annotation overlay when the ribbon's
  // "Start Presenting" command signals us. May not bridge separate
  // Office runtimes in all hosts; the slide reveal + student sync
  // happen in the command runtime regardless of this signal.
  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('gurohub-commands');
      channel.onmessage = (event: MessageEvent<{ command: string }>) => {
        if (event.data?.command === 'START_PRESENTER_MODE') {
          useAppStore.getState().setPresenterOverlayVisible(true);
        }
      };
    } catch {
      // BroadcastChannel unavailable; ignore.
    }
    return () => channel?.close();
  }, []);

  if (!isOfficeReady) {
    return (
      <div className="flex items-center justify-center h-full bg-surface">
        <LoadingSpinner size="lg" label="Loading GuroHub…" />
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Task Pane routes */}
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<TaskPane initialPanel="home" />} />
        <Route path="/quiz/mc" element={<TaskPane initialPanel="mc" />} />
        <Route path="/quiz/wordcloud" element={<TaskPane initialPanel="wordcloud" />} />
        <Route path="/quiz/shortanswer" element={<TaskPane initialPanel="shortanswer" />} />
        <Route path="/quiz/drawing" element={<TaskPane initialPanel="drawing" />} />
        <Route path="/quiz/media" element={<TaskPane initialPanel="media" />} />
        <Route path="/class" element={<TaskPane initialPanel="class" />} />
        <Route path="/leaderboard" element={<TaskPane initialPanel="leaderboard" />} />
        <Route path="/ai" element={<TaskPane initialPanel="ai" />} />

        {/* Student Portal routes */}
        <Route path="/join/*" element={<StudentApp />} />
        <Route path="/student/*" element={<StudentApp />} />
      </Routes>

      {/* Presenter overlay — rendered on top regardless of current route */}
      {presenterOverlayVisible && <PresenterOverlay />}
    </>
  );
}

function getInitialPath(): string {
  const hash = window.location.hash;
  return hash.startsWith('#') ? hash.slice(1) || '/' : '/';
}

export default function App(): React.ReactElement {
  return (
    <MemoryRouter initialEntries={[getInitialPath()]}>
      <AppInner />
    </MemoryRouter>
  );
}
